/**
 * Theme given to every ECharts chart of the application (ngx-echarts directive and echarts.init calls).
 *
 * It only enables aria: ECharts then writes a description of each chart (type, series, data) in the aria-label
 * of its container. Charts are drawn in a canvas, so this text is what screen readers and the UI tests of
 * ci/gitlab_test_ova.py can read. ECharts merges the non-component keys of a theme into every option, so the
 * charts do not have to set it themselves.
 */
export const ECHARTS_THEME = {
  aria: {
    enabled: true
  }
};

/** What appendItemCountsToAriaLabel uses of a chart: it accepts the charts of both 'echarts' and 'echarts/core',
 *  whose typings declare two different classes */
interface AnnotatedChart {
  on(eventName: 'finished', handler: () => void): unknown;
  getDom(): HTMLElement;
}

/** Part of ECharts' internal model read by appendItemCountsToAriaLabel (getModel() is not in the public typings) */
interface ChartWithModel {
  getModel(): { eachSeries(callback: (seriesModel: { getData(): { count(): number } }) => void): void } | null;
}

/**
 * The aria label of ECharts only lists the first values of each series, never how many there are. After each
 * render, this appends one sentence per series to the label, e.g. " Series 0 contains 24 items.", so that the
 * UI tests can check how many bars, slices or nodes were drawn. The counts are the ones ECharts' aria uses: the
 * data of each series (the nodes for a graph or a sankey, the dataset when the series has no data of its own).
 */
export function appendItemCountsToAriaLabel(chart: AnnotatedChart): void {
  chart.on('finished', () => {
    const model = (chart as unknown as ChartWithModel).getModel();
    const dom = chart.getDom();
    const label = dom?.getAttribute('aria-label');
    if (!model || !label) {
      return;
    }

    const sentences: string[] = [];
    model.eachSeries((seriesModel) => {
      sentences.push(` Series ${sentences.length} contains ${seriesModel.getData().count()} items.`);
    });
    const suffix = sentences.join('');

    // 'finished' also fires after a hover or a zoom, when ECharts has not rewritten the label
    if (suffix && !label.endsWith(suffix)) {
      dom.setAttribute('aria-label', label + suffix);
    }
  });
}
